import { useTranslation } from '@thoughtspot/i18n';
import { Icon, IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { canAnalyzeData, isExplainChangeEnabled, isKPIChartEnhancementsEnabled, } from '@thoughtspot/session-service';
import cx from 'classnames';
import { debounce, isNumber, noop } from 'lodash';
import React, { useCallback, useEffect, useLayoutEffect, useState, } from 'react';
import styles from '../../../styles/kpi.module.scss';
import { ShowChangeAs } from '../../../types';
import { ArrowIcon } from '../ArrowIcon';
import { getAbsoluteDiffValueToBeDisplayed, getAdditionalInlineStylesForComparison, getChangeIcon, isNumberInfinite, } from '../kpi-utils';
import { TEST_IDS } from './constants';
import { EmptyComparison } from './empty-comparison';
import { ellipsifyBlocks } from './utils';
const getDifferenceValueToDisplay = (absoluteDiffValue, formattedAbsoluteDifference, showChangeAs) => {
    if (isKPIChartEnhancementsEnabled() &&
        showChangeAs === ShowChangeAs.Absolute) {
        return formattedAbsoluteDifference;
    }
    return getAbsoluteDiffValueToBeDisplayed(absoluteDiffValue);
};
export const Comparison = React.memo(({ dateTime, containerRef, formattedBaseValue, formattedAbsoluteDifference, refTuple, diffValue, color, bgColor, isPresentModeOn, onClick, shouldShowExplainChange, hasAccessPermission, tooltip, isAnomalyV2Enabled, handleKeyPress, isAggregationEnabled, hideAnalyzeButton, kpiDisplayProperties, kpiColumnProperties, flags, }) => {
    const { isKPIChartEnhancementFeatureEnabled, isKPIWithAnomalyV2Enabled, } = flags;
    const { t } = useTranslation();
    const diffLabelRef = React.useRef(null);
    const baseRef = React.useRef(null);
    const timeBucketRef = React.useRef(null);
    const caretIconRef = React.useRef(null);
    const [compWidth, setCompWidth] = useState({
        containerWidth: 0,
        diffWidth: 0,
        baseWidth: 0,
        timeBucketWidth: 0,
    });
    const { showChange, showChangeAs } = kpiDisplayProperties;
    const { showComparisonDate, abbreviatedPreviousDate, showAbbreviatedPreviousDate, showPreviousDateLabel, showPreviousValue, } = kpiColumnProperties;
    const absoluteDiffValue = Math.abs(diffValue);
    const isDiffValueInfinite = isNumberInfinite(diffValue);
    const hasAnalyzePrivilege = canAnalyzeData();
    const isExplainChangeFeatureEnabled = isExplainChangeEnabled() &&
        shouldShowExplainChange &&
        hasAccessPermission;
    const getContainerWidth = useCallback((containerRef) => {
        var _a;
        if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) {
            return (containerRef.current.offsetWidth -
                (caretIconRef.current
                    ? caretIconRef.current.offsetWidth
                    : 0));
        }
        return 0;
    }, []);
    useEffect(() => {
        let resizeObserver;
        const debouncedUpdate = debounce(() => {
            setCompWidth((state) => ({
                ...state,
                containerWidth: getContainerWidth(containerRef),
            }));
        }, 300);
        if (containerRef.current) {
            resizeObserver = new ResizeObserver(debouncedUpdate);
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
            debouncedUpdate.cancel();
        };
    }, [containerRef, getContainerWidth]);
    useLayoutEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        setCompWidth({
            diffWidth: (_b = (_a = diffLabelRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0,
            baseWidth: (_d = (_c = baseRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth) !== null && _d !== void 0 ? _d : 0,
            timeBucketWidth: (_f = (_e = timeBucketRef.current) === null || _e === void 0 ? void 0 : _e.offsetWidth) !== null && _f !== void 0 ? _f : 0,
            containerWidth: getContainerWidth(containerRef),
        });
    }, [containerRef, getContainerWidth]);
    const elementEllipsis = React.useMemo(() => {
        return ellipsifyBlocks(compWidth.containerWidth, [
            compWidth.diffWidth,
            compWidth.baseWidth,
            compWidth.timeBucketWidth,
        ]);
    }, [compWidth]);
    const iconId = getChangeIcon(diffValue);
    const ariaLabel = iconId === IconID.ARROW_UP
        ? t('kpi_comparison_arrow_up')
        : t('kpi_comparison_arrow_down');
    const isAnomalyV2NotEnabled = isExplainChangeFeatureEnabled &&
        !isAnomalyV2Enabled &&
        hasAnalyzePrivilege;
    const isKPIWithAnomalyV2NotEnabled = !isKPIWithAnomalyV2Enabled &&
        (isExplainChangeFeatureEnabled || hideAnalyzeButton);
    const shouldShowVersus = isKPIChartEnhancementFeatureEnabled
        ? showComparisonDate && (showPreviousDateLabel || showPreviousValue)
        : !isKPIWithAnomalyV2NotEnabled;
    const shouldShowBaseValue = isKPIChartEnhancementFeatureEnabled
        ? showPreviousValue
        : true;
    const baseValue = !shouldShowBaseValue ? null : (React.createElement("div", { className: cx({
            [styles.base]: !isAggregationEnabled && isKPIWithAnomalyV2NotEnabled,
            [styles.timeBucket]: isAggregationEnabled,
            [styles.baseValueLabel]: !isKPIWithAnomalyV2NotEnabled,
            [styles.infiniteDiffValue]: isDiffValueInfinite,
            [styles.ellipsis]: elementEllipsis[1].isEllipsis,
            [styles.hidden]: elementEllipsis[1].isHidden,
        }), "data-testid": TEST_IDS.BASE_VALUE, ref: baseRef, style: {
            width: elementEllipsis[1].width,
        } },
        "(",
        formattedBaseValue,
        ")"));
    const baseCondition = !isAggregationEnabled;
    const shouldShowComparison = isKPIChartEnhancementFeatureEnabled
        ? baseCondition && showPreviousDateLabel
        : baseCondition;
    const shouldUseAbbreviatedDate = isKPIChartEnhancementFeatureEnabled &&
        showAbbreviatedPreviousDate &&
        abbreviatedPreviousDate != null;
    const dateTimeLabel = shouldUseAbbreviatedDate
        ? abbreviatedPreviousDate
        : dateTime;
    const comparisonTimeBucket = !shouldShowComparison ? null : (React.createElement("div", { className: cx(styles.timeBucket, {
            [styles.infiniteDiffValue]: isDiffValueInfinite,
            [styles.ellipsis]: elementEllipsis[2].isEllipsis,
            [styles.hidden]: elementEllipsis[2].isHidden,
        }), "data-testid": TEST_IDS.COMPARISON_TIME_BUCKET, ref: timeBucketRef, style: {
            width: elementEllipsis[2].width,
        } }, dateTimeLabel));
    const shouldShowComparisonBlock = !isKPIChartEnhancementFeatureEnabled || showComparisonDate;
    const comparisonContent = isKPIWithAnomalyV2Enabled ? (React.createElement(React.Fragment, null,
        comparisonTimeBucket,
        baseValue)) : (React.createElement(React.Fragment, null,
        baseValue,
        comparisonTimeBucket));
    const comparisonSection = (React.createElement(React.Fragment, null,
        shouldShowVersus && (React.createElement("span", { className: styles.versus, "data-testid": TEST_IDS.VERSUS }, `${t('VERSUS')} `)),
        shouldShowComparisonBlock && comparisonContent));
    const shouldDisplayIcon = isNumber(absoluteDiffValue) && absoluteDiffValue !== 0;
    const baseConditionComparisonDiffValue = !isNumberInfinite(absoluteDiffValue);
    const showComparisonDiffValue = isKPIChartEnhancementFeatureEnabled
        ? baseConditionComparisonDiffValue && showChange
        : baseConditionComparisonDiffValue;
    if (!refTuple || !refTuple.time) {
        return React.createElement(EmptyComparison, null);
    }
    return (React.createElement("div", { className: cx({
            [styles.comparison]: isKPIWithAnomalyV2NotEnabled,
            [styles.comparisonWithIcon]: isKPIWithAnomalyV2NotEnabled &&
                shouldDisplayIcon &&
                showComparisonDiffValue,
            [styles.comparisonAnomalyV2]: !isKPIWithAnomalyV2NotEnabled,
            [styles.comparisonPresent]: isPresentModeOn,
            [styles.backgroundColorComparison]: isAnomalyV2NotEnabled,
            [styles.infiniteDiffValue]: isDiffValueInfinite,
            [styles.liteLeftPadding]: isKPIWithAnomalyV2Enabled,
        }), "data-testid": TEST_IDS.COMPARISON, ...(!isAnomalyV2Enabled ? tooltip : {}), onClick: isAnomalyV2NotEnabled ? onClick : noop, onKeyDown: isAnomalyV2NotEnabled ? handleKeyPress : noop, role: !isAggregationEnabled ? 'button' : undefined, tabIndex: 0 },
        React.createElement("div", { className: styles.comparisonText },
            showComparisonDiffValue && (React.createElement("div", { className: cx(styles.difflabel, {
                    [styles.diffLabelAnomalyV2]: !isKPIWithAnomalyV2NotEnabled,
                    [styles.infiniteDiffValue]: isDiffValueInfinite,
                    [styles.ellipsis]: elementEllipsis[0].isEllipsis,
                }), style: {
                    color,
                    width: elementEllipsis[0].width,
                    ...getAdditionalInlineStylesForComparison({
                        absoluteDiffValue,
                        bgColor: bgColor !== null && bgColor !== void 0 ? bgColor : undefined,
                    }),
                }, "data-testid": TEST_IDS.DIFF_LABEL, ref: diffLabelRef },
                shouldDisplayIcon && (React.createElement(ArrowIcon, { color: color, iconId: iconId, ariaLabel: ariaLabel, isKPIWithAnomalyV2Enabled: isKPIWithAnomalyV2Enabled })),
                React.createElement("div", { className: cx(styles.diffValue, {
                        [styles.diffValueAnomalyV2]: isKPIWithAnomalyV2Enabled,
                        [styles.infiniteDiffValue]: isDiffValueInfinite,
                    }), "data-testid": TEST_IDS.DIFF }, getDifferenceValueToDisplay(absoluteDiffValue, formattedAbsoluteDifference, showChangeAs !== null && showChangeAs !== void 0 ? showChangeAs : undefined)))),
            comparisonSection),
        isAnomalyV2NotEnabled ? (React.createElement("div", { "data-testid": TEST_IDS.EXPLAIN_CHANGE_ICON, "data-tour-id": "tour_explain-change-icon", className: cx(styles.icon, {
                [styles.iconAnomalyV2]: isKPIWithAnomalyV2Enabled,
            }), ref: caretIconRef },
            React.createElement(Icon, { id: IconID.CHEVRON_RIGHT, color: IconColor.BLACK, size: IconSize.MEDIUM }))) : null));
});
//# sourceMappingURL=comparison.js.map